import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line, LabelList
} from 'recharts';
import { db, getCollectionName } from './firebase';
import { collection, onSnapshot, query, orderBy, getDocs, resumo } from './firestore-compat';
import { detectCol } from './dashboardUtils';
import { useBusinessHoursPolling, formatLastUpdated } from './hooks/useBusinessHoursPolling';
import './CensoHomecare.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseDateBR(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m1 = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m1) return new Date(+m1[3], +m1[2] - 1, +m1[1]);
  const m2 = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return new Date(+m2[1], +m2[2] - 1, +m2[3]);
  return null;
}

function normalizeStr(str) {
  if (!str) return '';
  return String(str).trim().toLowerCase();
}

function normClassif(val) {
  const s = normalizeStr(val).normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (!s) return 'OUTROS';
  if (/(^|[^a-z])id([^a-z]|$)/.test(s) || s.includes('internacao domiciliar')) return 'ID';
  if (/(^|[^a-z])ad([^a-z]|$)/.test(s) || s.includes('atendimento domiciliar')) return 'AD';
  if (s.includes('pontual')) return 'PONTUAL';
  if (s.includes('terapia') || s.includes('ppr')) return 'TERAPIAS';
  return 'OUTROS';
}

// Importações antigas do FTP leram o arquivo UTF-8 da IW como latin1 ("HospitalizaÃ§Ã£o").
// Conserta esses textos na hora de exibir/contar.
function consertarTexto(v) {
  if (typeof v !== 'string' || !/[ÃÂ]/.test(v)) return v;
  try { return decodeURIComponent(escape(v)); } catch { return v; }
}

// Normaliza "Alta complexidade com ventilação" / "Média complexidade" / etc. (como
// vem do Censo) para os rótulos usados no mapa de cores do gráfico de Complexidade
function normComplexidade(val) {
  const s = normalizeStr(val);
  if (!s) return 'Sem info';
  if (s.includes('alta')) return 'Alta Complexidade';
  if (s.includes('média') || s.includes('media')) return 'Média Complexidade';
  if (s.includes('baixa')) return 'Baixa Complexidade';
  return String(val).trim();
}

function normCidade(val) {
  const s = normalizeStr(val);
  if (!s) return '';
  if (s === 'rio de janeiro' || s === 'rj') return 'Rio de Janeiro';
  if (s === 'sao goncalo' || s.includes('goncalo') || s.includes('gonçalo')) return 'São Gonçalo';
  if (s === 'niteroi' || s.includes('niter')) return 'Niterói';
  return String(val).trim();
}

// Parseia "dataReferencia" do upload para o primeiro dia daquele mês
function parseDataReferencia(s) {
  if (!s) return null;
  const str = String(s).trim();
  const MESES = {
    'janeiro':0,'fevereiro':1,'março':2,'marco':2,'abril':3,'maio':4,
    'junho':5,'julho':6,'agosto':7,'setembro':8,'outubro':9,'novembro':10,'dezembro':11
  };
  const mNome = str.toLowerCase().match(/^([a-záéíóúâêîôûãõ]+)\s+(\d{4})$/);
  if (mNome && MESES[mNome[1]] !== undefined) return new Date(+mNome[2], MESES[mNome[1]], 1);
  const m1 = str.match(/^(\d{1,2})\/(\d{4})$/);
  if (m1) return new Date(+m1[2], +m1[1] - 1, 1);
  // Aceita tanto "YYYY-MM" quanto "YYYY-MM-DD" (dia é ignorado — vira o 1º dia do mês)
  const m2 = str.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
  if (m2) return new Date(+m2[1], +m2[2] - 1, 1);
  const m3 = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m3) return new Date(+m3[3], +m3[2] - 1, 1);
  return null;
}

// Deriva HomeBaby/Vitalcare a partir de vários campos possíveis.
// HomeSenior / Pronto Sênior agora é Vitalcare (pedido do Alexandre, 15/10/2026) — sempre mostrar "Vitalcare".
const ehVitalcare = s => /home ?senior|pronto ?s[eê]nior|vital ?care/.test(s);
function normUnidade(r, C) {
  // 1. HEALTHPROVIDERNAME do IW: "HomeSenior HC" ou "HomeBaby HC"
  const hp = String(r['HEALTHPROVIDERNAME'] || '').toLowerCase();
  if (hp.includes('homebaby') || hp.includes('home baby')) return 'HomeBaby';
  if (ehVitalcare(hp)) return 'Vitalcare';
  // 2. Coluna explícita de tipo de serviço (Tipo Serviço.1)
  if (C.tipoServico) {
    const v = String(r[C.tipoServico] || '').trim();
    if (v) return ehVitalcare(v.toLowerCase()) ? 'Vitalcare' : v.replace(/\s*HC\s*$/i, '').trim();
  }
  // 3. Campo _filial injetado de captacao_pacientes
  if (r._filial) {
    const f = String(r._filial).toLowerCase();
    if (f.includes('homebaby') || f.includes('home baby')) return 'HomeBaby';
    if (ehVitalcare(f)) return 'Vitalcare';
  }
  // 4. Prefixo da operadora (fallback legado)
  if (C.operadora) {
    const op = String(r[C.operadora] || '').trim().toUpperCase();
    if (op.startsWith('HS') || op.startsWith('VC')) return 'Vitalcare';
    if (op.startsWith('HB')) return 'HomeBaby';
  }
  return '';
}

// Agrupa uma lista de linhas (já com _dInternacao calculado) em série temporal
// por operadora — usado pelos gráficos de evolução (entradas, novos, reimplantações)
function buildEvolucaoSeries(rows, operadoraField, gran, mode) {
  const valid = rows.filter(r => r._dInternacao);
  if (!valid.length) return [];

  const getBk = d => {
    const dt = d._dInternacao;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return gran === 'M' ? `${y}-${m}` : `${y}-${m}-${day}`;
  };

  const buckets = [...new Set(valid.map(getBk))].sort();
  const opTotals = {};
  valid.forEach(d => {
    const op = d[operadoraField] || d['Operadora'] || '—';
    opTotals[op] = (opTotals[op] || 0) + 1;
  });
  const topOps = Object.entries(opTotals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(x => x[0]);
  const series = [...topOps];
  if (Object.keys(opTotals).length > 6) series.push('Outros');

  const counts = {};
  series.forEach(s => { counts[s] = {}; buckets.forEach(b => counts[s][b] = 0); });
  valid.forEach(d => {
    const op = d[operadoraField] || d['Operadora'] || '—';
    const s = topOps.includes(op) ? op : 'Outros';
    counts[s][getBk(d)]++;
  });

  const res = buckets.map(b => {
    const obj = { name: b };
    series.forEach(s => { obj[s] = counts[s][b]; });
    return obj;
  });

  if (mode === 'acum') {
    const running = {};
    series.forEach(s => running[s] = 0);
    res.forEach(row => { series.forEach(s => { running[s] += row[s]; row[s] = running[s]; }); });
  }

  const MES = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  return res.map(row => {
    let label = row.name;
    if (gran === 'M') label = `${MES[+label.slice(5,7)-1]}/${label.slice(2,4)}`;
    else label = `${label.slice(8,10)}/${label.slice(5,7)}/${label.slice(2,4)}`;
    return { ...row, label };
  });
}

const CLASSIF_KEYS = ['ID', 'AD', 'PONTUAL', 'TERAPIAS', 'OUTROS'];
const CLASSIFCOL = { ID: '#6366F1', AD: '#06B6D4', PONTUAL: '#F59E0B', TERAPIAS: '#8B5CF6', OUTROS: '#B0B6B2' };

const SITCOL = {
  "Em atendimento":   "#10B981",
  "Em avaliação":     "#F59E0B",
  "Alta/Saída":       "#7C8B8D",
  "Hospitalizado":    "#06B6D4",
  "Cancelado/Suspenso": "#8B5CF6",
  "Óbito":            "#EF4444"
};
const OPPAL = ["#6366F1","#06B6D4","#F59E0B","#FB7185","#8B5CF6","#10B981","#B0B6B2"];

// Statuses de captação que contam como "Em avaliação" no censo.
// "Em Implantação" e "Implantado" NÃO entram — esses pacientes já estão (ou estão prestes a estar) no FTP.
const CAPTACAO_EM_AVALIACAO = [
  'Recebido','Aguardando Avaliação','Em Avaliação','Avaliação Concluída',
  'Sessão Clínica','Relatório','Orçamento','Aguardando Aprovação da Direção',
  'Envio para Operadora','Aguardando Retorno da Operadora','Abordagem à Família'
];

// ── Sub-componentes ───────────────────────────────────────────────────────────
const LightTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1E293B', borderRadius: 10, padding: '10px 14px', fontSize: '13px', color: '#F1F5F9', zIndex: 1000 }}>
      {label && <div style={{ color: '#94A3B8', marginBottom: 4, fontWeight: 700 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#60A5FA', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span>{p.name}:</span>
          <strong>{typeof p.value === 'number' ? p.value.toLocaleString('pt-BR') : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

function RenderBarTable({ items, color, totalActive = 0, showLiminar = false, onRowClick, activeFilterLabel, mostrarTotal = true }) {
  if (!items || items.length === 0) {
    return <div className="empty">Sem dados para os filtros atuais.</div>;
  }
  const max = Math.max(1, ...items.map(x => x.total));

  return (
    <div className="btable">
      {items.map((x, i) => {
        let limText = null;
        if (showLiminar) {
          const p = x.total > 0 ? Math.round((x.comLiminar / x.total) * 100) : 0;
          limText = <span className="lm">{x.comLiminar} liminar · {p}%</span>;
        } else if (totalActive > 0) {
          const p = Math.round((x.total / totalActive) * 100);
          limText = <span style={{ color: 'var(--muted)', fontWeight: 500, fontSize: '11px' }}>{p}%</span>;
        }
        const isRowActive = activeFilterLabel === x.name;
        return (
          <div key={i} className="r" onClick={() => onRowClick && onRowClick(x.name)} style={{ cursor: onRowClick ? 'pointer' : 'default', background: isRowActive ? '#f0f4f8' : 'transparent', padding: '2px 4px', borderRadius: 4 }}>
            <div className="top">
              <span className="nm">{x.name}</span>
              <span className="ct num">{x.total.toLocaleString('pt-BR')} {limText}</span>
            </div>
            <div className="track">
              <i style={{ width: `${(x.total / max) * 100}%`, background: color }}></i>
            </div>
          </div>
        );
      })}
      {mostrarTotal && (
        <div className="top" style={{ borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 8, fontWeight: 800 }}>
          <span className="nm">Total</span>
          <span className="ct num">{items.reduce((s, x) => s + (x.total || 0), 0).toLocaleString('pt-BR')}</span>
        </div>
      )}
    </div>
  );
}

// Rosca com total no centro e legenda com quantidade e % de cada parte.
function RoscaComTotal({ data, cores, padrao = '#C9CFCB' }) {
  const total = data.reduce((s, x) => s + x.value, 0);
  return (
    <>
      <div className="chart-box sm" style={{ position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius="55%" outerRadius="90%" dataKey="value" nameKey="name" paddingAngle={2}>
              {data.map((e, i) => <Cell key={i} fill={cores[e.name] || padrao} />)}
            </Pie>
            <RTooltip content={<LightTip />} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span className="num" style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--ink)', lineHeight: 1 }}>{total.toLocaleString('pt-BR')}</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>total</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', justifyContent: 'center', marginTop: 8, fontSize: '0.78rem' }}>
        {data.map(e => (
          <span key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink)' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: cores[e.name] || padrao }} />
            {e.name}: <strong className="num">{e.value}</strong> <span style={{ color: 'var(--muted)' }}>({total ? Math.round(e.value / total * 100) : 0}%)</span>
          </span>
        ))}
      </div>
    </>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function DashboardIndicadores() {
  const [uploads, setUploads] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [uploadSel, setUploadSel] = useState(null);

  // Pacientes em avaliação vindos de captacao_pacientes
  const [captacaoRows, setCaptacaoRows] = useState([]);

  // Filtros
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [operadoraSel, setOperadoraSel] = useState('');
  const [liminarSel, setLiminarSel] = useState('');
  const [classifSel, setClassifSel] = useState('');
  const [statusSel, setStatusSel] = useState('Em atendimento');
  const [novoReSel, setNovoReSel] = useState('');
  const [complexidadeSel, setComplexidadeSel] = useState('');
  const [unidadeSel, setUnidadeSel] = useState('');

  // Evolução Configs
  const [evoGran, setEvoGran] = useState('M');
  const [evoMode, setEvoMode] = useState('periodo');

  const [cidadeSel, setCidadeSel] = useState('');
  const [redFlagSel, setRedFlagSel] = useState('');

  // KPI expandido (detalhamento por operadora)
  const [openKpi, setOpenKpi] = useState(null);

  // Drill-down
  const [drillFilter, setDrillFilter] = useState(null);
  const tableRef = useRef(null);
  const [tablePage, setTablePage] = useState(1);
  const POR_PAG = 25;

  // Carrega lista de uploads (metadados)
  useEffect(() => {
    const q = query(collection(db, 'indicadores_uploads'), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUploads(list);
      if (list.length > 0) {
        setUploadSel(prev => {
          const stillExists = prev && list.some(u => u.id === prev);
          return stillExists ? prev : list[0].id;
        });
      }
      setLoading(false);
    });
  }, []);

  // Carrega linhas do upload selecionado
  useEffect(() => {
    if (!uploadSel) return;
    setLoadingRows(true);
    setRows([]);
    getDocs(collection(db, 'indicadores_uploads', uploadSel, 'rows')).then(snap => {
      setRows(snap.docs.map(d => d.data()).sort((a, b) => (a._idx || 0) - (b._idx || 0)));
      setLoadingRows(false);
    });
  }, [uploadSel]);

  // Pacientes em avaliação + mapa Nº IW -> Tipo Implantação, ambos derivados de
  // UMA SÓ busca em captacao_pacientes (antes eram duas buscas idênticas da
  // tabela inteira, cada uma via onSnapshot poll a cada ~2min — ~95MB por poll,
  // dobrado). Atualiza sozinho de hora em hora em horário comercial; fora
  // disso só via botão "Atualizar".
  const [tipoImplantMap, setTipoImplantMap] = useState({});
  const carregarCaptacao = useCallback(async () => {
    const q = query(collection(db, getCollectionName('captacao_pacientes')), orderBy('dataAbertura', 'desc'), resumo('captacao_resumo'));
    const snap = await getDocs(q);
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    const emAvaliacao = all.filter(p => !p.excluido && !p.canceladoPelaOperadora && CAPTACAO_EM_AVALIACAO.includes(p.status));
    setCaptacaoRows(emAvaliacao);

    const map = {};
    all.forEach(p => {
      const key = String(p.numIW || p.numero || '').trim();
      if (key && p.tipoImplantacao) map[key] = p.tipoImplantacao;
    });
    setTipoImplantMap(map);
  }, []);
  const captacaoPolling = useBusinessHoursPolling(carregarCaptacao);

  const colunas = useMemo(() => {
    const up = uploads.find(u => u.id === uploadSel);
    const cols = (up?.colunas || []).filter(c => c !== '_idx');
    // DEBUG TEMPORÁRIO — auditoria de colunas do Censo Homecare
    console.log(`[AUDITORIA CENSO] upload atual: "${up?.fileName}" (dataReferencia=${up?.dataReferencia})`);
    console.log('[AUDITORIA CENSO] colunas:', cols);
    return cols;
  }, [uploads, uploadSel]);

  const C = useMemo(() => ({
    idAtend:          detectCol(colunas, ['id_atend']),
    prontuario:       detectCol(colunas, ['prontuario','prontuário']),
    status:           detectCol(colunas, ['status', 'situação', 'situacao']),
    dataInternacao:   detectCol(colunas, ['data interna','data internacao','data internação']),
    dataSaida:        detectCol(colunas, ['data da saida','data da saída','saida','saída','alta']),
    condicaoSaida:    detectCol(colunas, ['condição de saída','condicao de saida']),
    condicaoAltaTiss: detectCol(colunas, ['condição de alta tiss','condicao de alta tiss']),
    liminar:          colunas.find(c => c === 'Liminar') || detectCol(colunas, ['liminar']),
    // ADMISSIONTYPE é o campo do IW que indica Novo Paciente / Re-Implantação
    pacientType:      detectCol(colunas, ['pacienttype','tipo paciente','admissiontype']),
    // Programa de atendimento do IW (ID/AD/Pontual). A coluna amigável das importações do
    // FTP guardava Adulto/Pediatria e a busca por "classific" pegava a classificação de risco.
    classif:          colunas.includes('ATTENDANCEPROGRAM') ? 'ATTENDANCEPROGRAM'
                        : (colunas.find(c => c === 'Classific. Atendimento') ?? detectCol(colunas, ['classific. atendimento','classificação','classific'])),
    complexidade:     detectCol(colunas, ['complexidade']),
    operadora:        colunas.find(c => c === 'Operadora') ?? detectCol(colunas, ['convenio','convênio']),
    tipoServico:      colunas.find(c => c === 'Tipo Serviço.1') || colunas.find(c => c === 'Tipo Servico.1') || null,
    // Match exato primeiro — 'detectCol' por substring pegaria "Criticidade do Monitoramento"
    cidade:           colunas.find(c => c === 'Cidade') ?? detectCol(colunas, ['cidade']),
    redFlag:          detectCol(colunas, ['paciente red flag','red flag']),
    dispositivos:     colunas.filter(c => c.toLowerCase().includes('tem gtt') || c.toLowerCase().includes('tem tqt') || c.toLowerCase().includes('tem sne') || c.toLowerCase().includes('tem cvd') || c.toLowerCase().includes('tem picc') || c.toLowerCase().includes('tem colostomia') || c.toLowerCase().includes('tem ileostomia') || c.toLowerCase().includes('tem port') || c.toLowerCase().includes('tem lesão') || c.toLowerCase().includes('usa oxig')),
  }), [colunas]);

  // Normaliza linhas do FTP
  const normalizedFtpRows = useMemo(() => {
    return rows.map(r0 => {
      const r = Object.fromEntries(Object.entries(r0).map(([k, v]) => [k, consertarTexto(v)]));
      const nClassif = normClassif(r[C.classif]);
      let nLiminar = String(r[C.liminar] || '').trim();
      if (nLiminar !== 'Sim' && nLiminar !== 'Não') nLiminar = 'Não Informado';
      const nCidade = normCidade(r[C.cidade]);
      const nUnidade = normUnidade(r, C);

      // Novo × Re-implantação: usa o campo bruto do IW quando presente; senão,
      // cai para o tipoImplantacao registrado na Captação (casado pelo Nº IW).
      // Captação grava "Novo"/"Reimplantação" — normaliza pros mesmos rótulos do IW.
      const idAtendVal = String((C.idAtend && r[C.idAtend]) || '').trim();
      const tipoCaptacao = tipoImplantMap[idAtendVal];
      const nTipoCaptacao = tipoCaptacao === 'Novo' ? 'Novo Paciente'
        : tipoCaptacao === 'Reimplantação' ? 'Re-Implantação'
        : (tipoCaptacao || '');
      // ADMISSIONTYPE vem "Home Care" para todos — só vale se disser Novo/Re-implantação.
      const tipoIW = String((C.pacientType && r[C.pacientType]) || '').trim();
      const nPacientType = (/novo|re-?impl/i.test(tipoIW) ? tipoIW : '') || nTipoCaptacao || 'Sem info';

      return {
        ...r,
        _fonte: r._fonte || 'ftp',
        _nClassif: nClassif,
        _nLiminar: nLiminar,
        _nCidade: nCidade,
        _nUnidade: nUnidade,
        _nPacientType: nPacientType,
        _dInternacao: parseDateBR(r[C.dataInternacao]),
        _dSaida: parseDateBR(r[C.dataSaida]),
      };
    });
  }, [rows, C, tipoImplantMap]);

  // Normaliza pacientes de captação como linhas "em avaliação"
  const normalizedCaptacaoRows = useMemo(() => {
    return captacaoRows.map(p => {
      let nUnidade = '';
      const f = String(p.filial || '').toLowerCase();
      if (f.includes('homebaby') || f.includes('home baby')) nUnidade = 'HomeBaby';
      else if (ehVitalcare(f)) nUnidade = 'Vitalcare';

      let nLiminar = 'Não Informado';
      if (p.liminar === true || String(p.liminar).toLowerCase() === 'sim') nLiminar = 'Sim';
      else if (p.liminar === false || String(p.liminar).toLowerCase() === 'não') nLiminar = 'Não';

      const dSolic = parseDateBR(p.dataSolicitacao) || (p.dataAbertura?.toDate ? p.dataAbertura.toDate() : null);

      return {
        // Campos compatíveis com o schema FTP para os filtros e tabela funcionarem
        _fonte: 'captacao',
        _captacaoId: p.id,
        'Id Atend': p.numIW || p.numero || p.id,
        'Nome': p.nome || '',
        'Operadora': p.convenio || '',
        'Status': 'Em avaliação',
        'Data Internação': p.dataSolicitacao || '',
        'Liminar': nLiminar,
        'Cidade': '',
        'Classific. Atendimento': p.tipoImplantacao || '',
        // campos internos
        _nClassif: normClassif(p.tipoImplantacao),
        _nLiminar: nLiminar,
        _nCidade: '',
        _nUnidade: nUnidade,
        _dInternacao: dSolic,
        _dSaida: null,
        // campo para mostrar status de captação na tabela
        _statusCaptacao: p.status,
      };
    });
  }, [captacaoRows]);

  // Todas as linhas combinadas (FTP + captação em avaliação)
  const normalizedRows = useMemo(() => {
    return [...normalizedFtpRows, ...normalizedCaptacaoRows];
  }, [normalizedFtpRows, normalizedCaptacaoRows]);

  // Deriva o mês de referência do upload selecionado (para usar como período padrão)
  const uploadDataRef = useMemo(() => {
    const up = uploads.find(u => u.id === uploadSel);
    return parseDataReferencia(up?.dataReferencia);
  }, [uploads, uploadSel]);

  const currentPeriod = useMemo(() => {
    const now = new Date();
    const defStart = uploadDataRef || new Date(now.getFullYear(), now.getMonth(), 1);
    const defEnd = uploadDataRef
      ? new Date(uploadDataRef.getFullYear(), uploadDataRef.getMonth() + 1, 0)
      : new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const start = periodoInicio ? parseDateBR(periodoInicio) : defStart;
    const end = periodoFim ? parseDateBR(periodoFim) : new Date(defEnd);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [periodoInicio, periodoFim, uploadDataRef]);

  // filtered: aplica todos os filtros EXCETO statusSel + computa _sit
  // Assim, KPIs e gráficos sempre enxergam todos os status
  const filtered = useMemo(() => {
    let data = normalizedRows;
    const _defStart = uploadDataRef || new Date('2000-01-01');
    const _defEnd = uploadDataRef
      ? new Date(uploadDataRef.getFullYear(), uploadDataRef.getMonth() + 1, 0)
      : new Date('2100-01-01');
    const start = periodoInicio ? parseDateBR(periodoInicio) : _defStart;
    const end = periodoFim ? parseDateBR(periodoFim) : _defEnd;
    end.setHours(23, 59, 59, 999);

    data = data.filter(r => {
      if (r._dInternacao && r._dInternacao > end) return false;
      if (r._dSaida && r._dSaida < start) return false;
      return true;
    }).map(r => {
      // Paciente de captação: status já é fixo "Em avaliação"
      if (r._fonte === 'captacao') {
        return { ...r, _efStatus: 'Em avaliação', _efCond: '', _sit: 'Em avaliação' };
      }

      let efStatus = String(r[C.status] || '').trim();
      // Alguns exports do Censo trazem o status como código numérico do IW (0/1/2)
      // em vez do texto — normaliza pro mesmo vocabulário usado no resto do dashboard.
      if (efStatus === '0') efStatus = 'Em avaliação';
      else if (efStatus === '1') efStatus = 'Em atendimento';
      else if (efStatus === '2') efStatus = 'Alta/Saída';
      let efCond = String(r[C.condicaoSaida] || '').trim();

      if (r._dSaida && r._dSaida > end) {
        efStatus = 'Em atendimento';
        efCond = '';
      }

      let sit = 'Alta/Saída';
      const efl = efStatus.toLowerCase();
      // "Em avaliação" normalmente vem só de captacao_pacientes, mas também pode vir
      // do código IW "0" normalizado acima (paciente aguardando internação no Censo)
      if (efl.includes('atendimento'))      sit = 'Em atendimento';
      else if (efl.includes('avaliação') || efl.includes('avaliacao')) sit = 'Em avaliação';
      else if (!efStatus && !r._dSaida)    sit = 'Em atendimento';
      else if (['Cancelado pelo convenio','Suspenso pela Operadora','Orçamento Inválido'].includes(efCond)) sit = 'Cancelado/Suspenso';
      else if (['Óbito','Óbito após hospitalização'].includes(efCond))                                     sit = 'Óbito';
      else if (['Hospitalização','Alta após hospitalização','Mudança H.C após Hospitalização'].includes(efCond)) sit = 'Hospitalizado';

      return { ...r, _efStatus: efStatus, _efCond: efCond, _sit: sit };
    });

    if (operadoraSel !== '') data = data.filter(r => (r[C.operadora] || r['Operadora']) === operadoraSel);
    if (liminarSel !== '')   data = data.filter(r => r._nLiminar === liminarSel);
    if (classifSel !== '')   data = data.filter(r => r._nClassif === classifSel);
    if (complexidadeSel !== '' && C.complexidade) data = data.filter(r => r[C.complexidade] === complexidadeSel);
    if (unidadeSel !== '')   data = data.filter(r => r._nUnidade === unidadeSel);
    if (novoReSel !== '' && C.pacientType) {
      if (novoReSel === 'Novo Paciente') data = data.filter(r => String(r._nPacientType || '').toLowerCase().includes('novo'));
      else data = data.filter(r => String(r._nPacientType || '').toLowerCase().includes('re'));
    }
    if (cidadeSel !== '') data = data.filter(r => r._nCidade === cidadeSel);
    if (redFlagSel !== '' && C.redFlag) data = data.filter(r => String(r[C.redFlag] || '').trim() === redFlagSel);

    return data;
  }, [normalizedRows, periodoInicio, periodoFim, uploadDataRef, operadoraSel, liminarSel, classifSel, novoReSel, complexidadeSel, unidadeSel, cidadeSel, redFlagSel, C]);

  // statusFiltered: filtered + statusSel (para tabela e cabeçalho)
  const statusFiltered = useMemo(() => {
    if (statusSel === 'Em atendimento') return filtered.filter(r => r._sit === 'Em atendimento');
    if (statusSel === 'Em avaliação')   return filtered.filter(r => r._sit === 'Em avaliação');
    if (statusSel === 'Alta')           return filtered.filter(r => r._sit === 'Alta/Saída');
    return filtered;
  }, [filtered, statusSel]);

  // KPIs — sempre de `filtered` (todos status), usa _sit para consistência
  const kpis = useMemo(() => {
    let emAtendimento = 0, emAvaliacao = 0, comLiminar = 0;
    let entradasMes = 0, entradasMesNovos = 0, entradasMesReimplant = 0, altasMes = 0;

    filtered.forEach(r => {
      if (r._sit === 'Em atendimento') {
        emAtendimento++;
        if (r._nLiminar === 'Sim') comLiminar++;
      } else if (r._sit === 'Em avaliação') {
        emAvaliacao++;
      }

      if (r._dInternacao && r._dInternacao >= currentPeriod.start && r._dInternacao <= currentPeriod.end) {
        entradasMes++;
        const pt = String(r._nPacientType || '').toLowerCase();
        if (pt.includes('novo'))      entradasMesNovos++;
        else if (pt.includes('re'))   entradasMesReimplant++;
        // pacientes sem tipo não são contados como nenhum dos dois
      }

      if (r._dSaida && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end) {
        altasMes++;
      }
    });

    let redFlagAtivos = 0;
    if (C.redFlag) {
      filtered.forEach(r => {
        if (r._sit === 'Em atendimento' && String(r[C.redFlag] || '').trim() === 'Sim') redFlagAtivos++;
      });
    }

    return { emAtendimento, emAvaliacao, comLiminar, entradasMes, entradasMesNovos, entradasMesReimplant, altasMes, redFlagAtivos };
  }, [filtered, C, currentPeriod]);

  // Opções de Filtro
  const optsConvenio     = useMemo(() => {
    const ftpOps = normalizedFtpRows.map(r => r[C.operadora]).filter(Boolean);
    const capOps = captacaoRows.map(p => p.convenio).filter(Boolean);
    return [...new Set([...ftpOps, ...capOps])].sort();
  }, [normalizedFtpRows, captacaoRows, C.operadora]);

  const optsClassif      = ['ID', 'AD', 'PONTUAL', 'TERAPIAS', 'OUTROS'];
  const optsComplexidade = useMemo(() => [...new Set(normalizedFtpRows.map(r => r[C.complexidade]).filter(Boolean))].sort(), [normalizedFtpRows, C.complexidade]);
  const optsUnidade      = useMemo(() => [...new Set(normalizedRows.map(r => r._nUnidade).filter(Boolean))].sort(), [normalizedRows]);
  const optsCidades      = useMemo(() => [...new Set(normalizedRows.map(r => r._nCidade).filter(Boolean))].sort(), [normalizedRows]);

  // Ativos (sem statusSel) — base para gráficos de perfil de pacientes
  const ativosFiltered = useMemo(() => filtered.filter(r => r._sit === 'Em atendimento'), [filtered]);

  const chartClassif = useMemo(() => {
    const map = {};
    ativosFiltered.forEach(r => {
      const v = r._nClassif;
      if (!map[v]) map[v] = { total: 0, comLiminar: 0 };
      map[v].total++;
      if (r._nLiminar === 'Sim') map[v].comLiminar++;
    });
    return Object.entries(map).map(([name, d]) => ({ name, ...d })).sort((a, b) => b.total - a.total);
  }, [ativosFiltered]);

  // Classificação (ID/AD/Pontual/Terapias/Outros) por unidade — todos os orçamentos filtrados
  const classifPorFilial = useMemo(() => {
    const unidades = [...new Set(filtered.map(r => r._nUnidade).filter(Boolean))].sort();
    return unidades.map(u => {
      const row = { name: u };
      CLASSIF_KEYS.forEach(k => row[k] = 0);
      filtered.filter(r => r._nUnidade === u).forEach(r => {
        if (row[r._nClassif] !== undefined) row[r._nClassif]++;
      });
      return row;
    });
  }, [filtered]);

  const topOperadoras = useMemo(() => {
    const map = {};
    ativosFiltered.forEach(r => {
      const v = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [ativosFiltered, C]);

  // Novos pacientes por operadora (entradas no período)
  const topNovosPorOperadora = useMemo(() => {
    const entradas = filtered.filter(r =>
      r._dInternacao && r._dInternacao >= currentPeriod.start && r._dInternacao <= currentPeriod.end
    );
    const map = {};
    entradas.forEach(r => {
      const v = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [filtered, currentPeriod, C]);

  // Reimplantados por operadora (entradas no período, tipo Re-Implantação)
  const topReimplantPorOperadora = useMemo(() => {
    const entradas = filtered.filter(r => {
      if (!r._dInternacao || r._dInternacao < currentPeriod.start || r._dInternacao > currentPeriod.end) return false;
      const t = String(r._nPacientType || '').toLowerCase();
      return !t.includes('novo') && t.includes('re');
    });
    const map = {};
    entradas.forEach(r => {
      const v = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [filtered, currentPeriod, C]);

  // Todas as entradas do período por operadora (novos + reimplant. + sem tipo) — breakdown do KPI "Entradas"
  const entradasPorOperadora = useMemo(() => {
    const entradas = filtered.filter(r => r._dInternacao && r._dInternacao >= currentPeriod.start && r._dInternacao <= currentPeriod.end);
    const map = {};
    entradas.forEach(r => {
      const v = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [filtered, currentPeriod, C]);

  // Altas / saídas do período por operadora
  const altasPorOperadora = useMemo(() => {
    const altas = filtered.filter(r => r._dSaida && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end);
    const map = {};
    altas.forEach(r => {
      const v = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [filtered, currentPeriod, C]);

  // Ativos com liminar por operadora — breakdown do KPI "Com liminar"
  const liminarPorOperadora = useMemo(() => {
    const map = {};
    ativosFiltered.filter(r => r._nLiminar === 'Sim').forEach(r => {
      const v = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [ativosFiltered, C]);

  // Solicitações por operadora (captação — respeita todos os filtros)
  const solicitacoesPorOperadora = useMemo(() => {
    const map = {};
    filtered.filter(r => r._fonte === 'captacao').forEach(r => {
      const v = r['Operadora'] || 'Não Informado';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [filtered]);

  const chartDispositivos = useMemo(() => {
    const map = {};
    C.dispositivos.forEach(d => map[d] = 0);
    ativosFiltered.forEach(r => {
      C.dispositivos.forEach(d => {
        if (String(r[d] || '').trim() === 'Sim') {
          const cleanName = d.replace(/tem /i, '').trim();
          map[cleanName] = (map[cleanName] || 0) + 1;
        }
      });
    });
    return Object.entries(map)
      .filter(([, total]) => total > 0)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [ativosFiltered, C.dispositivos]);

  const chartComplexidade = useMemo(() => {
    if (!C.complexidade) return [];
    const map = {};
    ativosFiltered.forEach(r => {
      const v = normComplexidade(r[C.complexidade]);
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [ativosFiltered, C.complexidade]);

  const chartNovoRe = useMemo(() => {
    const map = {};
    ativosFiltered.forEach(r => {
      const v = r._nPacientType || '—';
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [ativosFiltered]);

  // DEBUG TEMPORÁRIO — auditoria de Complexidade / Novo×Reimplant
  useEffect(() => {
    console.log('[AUDITORIA CENSO] C.complexidade (coluna detectada):', C.complexidade);
    console.log('[AUDITORIA CENSO] C.pacientType (coluna detectada):', C.pacientType);
    console.log('[AUDITORIA CENSO] C.idAtend (coluna detectada):', C.idAtend);
    console.log('[AUDITORIA CENSO] ativosFiltered.length:', ativosFiltered.length);
    console.log('[AUDITORIA CENSO] tipoImplantMap tamanho:', Object.keys(tipoImplantMap).length);
    console.log('[AUDITORIA CENSO] chartComplexidade:', chartComplexidade);
    console.log('[AUDITORIA CENSO] chartNovoRe:', chartNovoRe);
    console.log('[AUDITORIA CENSO] amostra 3 linhas ativas cruas:', ativosFiltered.slice(0, 3));
  }, [C, ativosFiltered, tipoImplantMap, chartComplexidade, chartNovoRe]);

  const chartCondicaoSaida = useMemo(() => {
    const saidas = filtered.filter(r =>
      r._dSaida && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end &&
      r[C.condicaoSaida] && r[C.condicaoSaida] !== '—'
    );
    const map = {};
    saidas.forEach(r => { const v = r[C.condicaoSaida]; map[v] = (map[v] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered, currentPeriod, C.condicaoSaida]);

  const chartAltaTiss = useMemo(() => {
    const altas = filtered.filter(r =>
      r._dSaida && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end && r[C.condicaoAltaTiss]
    );
    const map = {};
    altas.forEach(r => { const v = r[C.condicaoAltaTiss]; map[v] = (map[v] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered, currentPeriod, C.condicaoAltaTiss]);

  // ── Métricas de qualidade e gestão ───────────────────────────────────────────

  const taxaDesfecho = useMemo(() => {
    const altas = filtered.filter(r => r._dSaida && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end);
    const totalAltas = altas.length;
    const hosp   = altas.filter(r => ['Hospitalização','Alta após hospitalização','Mudança H.C após Hospitalização'].includes(r._efCond || '')).length;
    const obito  = altas.filter(r => ['Óbito','Óbito após hospitalização'].includes(r._efCond || '')).length;
    const cancel = altas.filter(r => ['Cancelado pelo convenio','Suspenso pela Operadora','Orçamento Inválido'].includes(r._efCond || '')).length;
    return {
      totalAltas,
      hosp,   taxaHosp:   totalAltas ? +((hosp   / totalAltas) * 100).toFixed(1) : 0,
      obito,  taxaObito:  totalAltas ? +((obito  / totalAltas) * 100).toFixed(1) : 0,
      cancel, taxaCancel: totalAltas ? +((cancel / totalAltas) * 100).toFixed(1) : 0,
    };
  }, [filtered, currentPeriod]);

  const permanenciaStats = useMemo(() => {
    const dias = filtered
      .filter(r => r._dSaida && r._dInternacao && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end)
      .map(r => Math.round((r._dSaida - r._dInternacao) / (1000 * 60 * 60 * 24)))
      .filter(d => d >= 0)
      .sort((a, b) => a - b);
    if (!dias.length) return { media: null, mediana: null, total: 0 };
    return {
      media:   Math.round(dias.reduce((s, v) => s + v, 0) / dias.length),
      mediana: dias[Math.floor(dias.length / 2)],
      total:   dias.length,
    };
  }, [filtered, currentPeriod]);

  const permanenciaPorComplexidade = useMemo(() => {
    if (!C.complexidade) return [];
    const map = {};
    filtered.filter(r => r._dSaida && r._dInternacao && r[C.complexidade]).forEach(r => {
      const v = normComplexidade(r[C.complexidade]);
      if (!map[v]) map[v] = [];
      const d = Math.round((r._dSaida - r._dInternacao) / (1000 * 60 * 60 * 24));
      if (d >= 0) map[v].push(d);
    });
    return Object.entries(map)
      .map(([name, arr]) => ({ name, media: Math.round(arr.reduce((s, v) => s + v, 0) / arr.length), total: arr.length }))
      .sort((a, b) => b.media - a.media);
  }, [filtered, C.complexidade]);

  const permanenciaPorOperadora = useMemo(() => {
    if (!C.operadora) return [];
    const map = {};
    filtered.filter(r => r._dSaida && r._dInternacao).forEach(r => {
      const op = r[C.operadora] || r['Operadora'] || 'Não Informado';
      if (!map[op]) map[op] = [];
      const d = Math.round((r._dSaida - r._dInternacao) / (1000 * 60 * 60 * 24));
      if (d >= 0) map[op].push(d);
    });
    return Object.entries(map)
      .filter(([, arr]) => arr.length >= 3)
      .map(([name, arr]) => ({ name, media: Math.round(arr.reduce((s, v) => s + v, 0) / arr.length), total: arr.length }))
      .sort((a, b) => b.media - a.media)
      .slice(0, 10);
  }, [filtered, C.operadora]);

  const saldoPorMes = useMemo(() => {
    const buckets = {};
    filtered.forEach(r => {
      if (r._dInternacao) {
        const k = `${r._dInternacao.getFullYear()}-${String(r._dInternacao.getMonth() + 1).padStart(2, '0')}`;
        if (!buckets[k]) buckets[k] = { entradas: 0, altas: 0 };
        buckets[k].entradas++;
      }
      if (r._dSaida) {
        const k = `${r._dSaida.getFullYear()}-${String(r._dSaida.getMonth() + 1).padStart(2, '0')}`;
        if (!buckets[k]) buckets[k] = { entradas: 0, altas: 0 };
        buckets[k].altas++;
      }
    });
    const MES = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({
        label:    `${MES[+k.slice(5, 7) - 1]}/${k.slice(2, 4)}`,
        Entradas: v.entradas,
        Altas:    v.altas,
        Saldo:    v.entradas - v.altas,
      }));
  }, [filtered]);

  const chartCidades = useMemo(() => {
    const map = {};
    ativosFiltered.forEach(r => { if (r._nCidade) map[r._nCidade] = (map[r._nCidade] || 0) + 1; });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total);
  }, [ativosFiltered]);

  const redFlagPorOperadora = useMemo(() => {
    if (!C.redFlag) return [];
    const map = {};
    ativosFiltered.filter(r => String(r[C.redFlag] || '').trim() === 'Sim').forEach(r => {
      const op = r[C.operadora] || r['Operadora'] || 'Não Informado';
      map[op] = (map[op] || 0) + 1;
    });
    return Object.entries(map).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [ativosFiltered, C.redFlag, C.operadora]);

  const multiDispositivosTotal = useMemo(() => {
    if (!C.dispositivos.length) return 0;
    return ativosFiltered.filter(r => C.dispositivos.filter(d => String(r[d] || '').trim() === 'Sim').length >= 2).length;
  }, [ativosFiltered, C.dispositivos]);

  const DESFECHO_KEYS = ['Em avaliação', 'Em atendimento', 'Hospitalizado', 'Alta/Saída', 'Cancelado/Suspenso', 'Óbito'];
  const chartEntradasOperadora = useMemo(() => {
    if (!C.operadora) return [];
    const entradas = filtered.filter(r =>
      r._dInternacao && r._dInternacao >= currentPeriod.start && r._dInternacao <= currentPeriod.end
    );
    const opCount = {};
    entradas.forEach(r => {
      const op = r[C.operadora] || r['Operadora'] || '—';
      opCount[op] = (opCount[op] || 0) + 1;
    });
    const topOps = Object.entries(opCount).sort((a, b) => b[1] - a[1]).slice(0, 8).map(x => x[0]);
    return topOps.map(op => {
      const row = { name: op };
      DESFECHO_KEYS.forEach(k => row[k] = 0);
      entradas.filter(r => (r[C.operadora] || r['Operadora'] || '—') === op).forEach(r => {
        if (row[r._sit] !== undefined) row[r._sit]++;
      });
      return row;
    });
  }, [filtered, currentPeriod, C.operadora]);

  const chartEvolucao = useMemo(
    () => buildEvolucaoSeries(filtered, C.operadora, evoGran, evoMode),
    [filtered, C.operadora, evoGran, evoMode]
  );

  const evolucaoSeries = chartEvolucao.length > 0
    ? Object.keys(chartEvolucao[0]).filter(k => k !== 'name' && k !== 'label')
    : [];

  // Entradas de novos pacientes por mês (série por operadora)
  const novosEvolucao = useMemo(() => {
    const novos = filtered.filter(r => String(r._nPacientType || '').toLowerCase().includes('novo'));
    return buildEvolucaoSeries(novos, C.operadora, 'M', 'periodo');
  }, [filtered, C.operadora]);
  const novosEvolucaoSeries = novosEvolucao.length > 0
    ? Object.keys(novosEvolucao[0]).filter(k => k !== 'name' && k !== 'label')
    : [];

  // Reimplantações (reentradas) por mês (série por operadora)
  const reimplantEvolucao = useMemo(() => {
    const re = filtered.filter(r => {
      const t = String(r._nPacientType || '').toLowerCase();
      return !t.includes('novo') && t.includes('re');
    });
    return buildEvolucaoSeries(re, C.operadora, 'M', 'periodo');
  }, [filtered, C.operadora]);
  const reimplantEvolucaoSeries = reimplantEvolucao.length > 0
    ? Object.keys(reimplantEvolucao[0]).filter(k => k !== 'name' && k !== 'label')
    : [];

  // Drill-down usa statusFiltered como base
  const drillData = useMemo(() => {
    if (!drillFilter) return statusFiltered;
    return statusFiltered.filter(drillFilter.fn);
  }, [statusFiltered, drillFilter]);

  const totalPag = Math.max(1, Math.ceil(drillData.length / POR_PAG));
  const rowsPag = drillData.slice((tablePage - 1) * POR_PAG, tablePage * POR_PAG);

  const toggleDrill = useCallback((fn, label) => {
    setDrillFilter(prev => prev?.label === label ? null : { fn, label });
    setTablePage(1);
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
  }, []);

  const limparFiltros = () => {
    setPeriodoInicio(''); setPeriodoFim(''); setOperadoraSel('');
    setLiminarSel(''); setClassifSel(''); setStatusSel('Em atendimento');
    setNovoReSel(''); setComplexidadeSel(''); setUnidadeSel('');
    setCidadeSel(''); setRedFlagSel('');
    setDrillFilter(null); setTablePage(1);
  };

  const _MES_ABBR = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  const periodTxt = (periodoInicio || periodoFim)
    ? "no período"
    : uploadDataRef
      ? `${_MES_ABBR[uploadDataRef.getMonth()]}/${String(uploadDataRef.getFullYear()).slice(2)}`
      : "mês atual";
  const stlMap = { "Em atendimento": "ativos", "Em avaliação": "em avaliação", "Alta": "com alta", "": "todos os status" };
  const stl = stlMap[statusSel] || "todos";

  // HomeBaby/Vitalcare contados a partir do statusFiltered (respeita filtro de status)
  const babyCount = statusFiltered.filter(r => r._nUnidade === 'HomeBaby').length;
  const senCount  = statusFiltered.filter(r => r._nUnidade === 'Vitalcare').length;

  // Colunas para a tabela (FTP ou captação)
  const tabelaColunas = useMemo(() => {
    if (!statusFiltered.length) return colunas.length ? colunas : [];
    // Se há linhas de captação visíveis, usa colunas amigáveis
    const hasCap = statusFiltered.some(r => r._fonte === 'captacao');
    if (hasCap && !colunas.length) {
      return ['Id Atend','Nome','Operadora','Status','_statusCaptacao','Data Internação','Liminar'];
    }
    return colunas;
  }, [statusFiltered, colunas]);

  if (loading) return (
    <div className="censo-homecare" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}><div style={{ fontSize: '3rem', marginBottom: 12 }}>📊</div><div style={{ color: 'var(--indigo)', fontWeight: 700 }}>Carregando...</div></div>
    </div>
  );

  const uploadAtual = uploads.find(u => u.id === uploadSel);

  return (
    <div className="censo-homecare">
    <div className="wrap">

      <div className="topbar">
        <div className="brand">
          <div className="logo"></div>
          <div>
            <h1>Censo Homecare</h1>
            <p>HomeBaby &amp; Vitalcare — pacientes e orçamentos</p>
          </div>
        </div>
        <div className="spacer"></div>
        <select className="top-sel" value={uploadSel || ''} onChange={e => { setUploadSel(e.target.value); setDrillFilter(null); }}>
          {uploads.map(u => <option key={u.id} value={u.id}>{u.fileName} — {u.dataReferencia}</option>)}
        </select>
        <button className="btn" onClick={captacaoPolling.refresh} disabled={captacaoPolling.refreshing}
          title="Dados de captação atualizam sozinhos de hora em hora em horário comercial — clique para forçar agora"
          style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.78rem' }}>
          {captacaoPolling.refreshing ? '⏳' : '🔄'} Captação {captacaoPolling.lastUpdated ? `· ${formatLastUpdated(captacaoPolling.lastUpdated)}` : ''}
        </button>
        <Link to="/uploads?tab=indicadores" className="btn btn-primary">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Atualizar dados
        </Link>
      </div>

      <div className="section-h" style={{ marginTop: 0 }}>
        <h2>{statusFiltered.length.toLocaleString('pt-BR')} pacientes {stl}</h2>
        <span className="desc">HomeBaby {babyCount} · Vitalcare {senCount}{uploadAtual ? ` · atualizado por ${uploadAtual.uploadedBy} em ${new Date(uploadAtual.timestamp).toLocaleDateString('pt-BR')}` : ''}</span>
      </div>

      <div className="filters">
        {optsUnidade.length > 0 && (
          <div className="fgroup"><label>Unidade</label>
            <div className="chips">
              <span className={`chip ${unidadeSel === '' ? 'on' : ''}`} onClick={() => setUnidadeSel('')}>Todas</span>
              {optsUnidade.map(v => <span key={v} className={`chip ${unidadeSel === v ? 'on' : ''}`} onClick={() => setUnidadeSel(v)}>{v}</span>)}
            </div>
          </div>
        )}
        <div className="fgroup"><label>Status</label>
          <div className="chips">
            <span className={`chip ${statusSel === '' ? 'on' : ''}`} onClick={() => setStatusSel('')}>Todos</span>
            <span className={`chip ${statusSel === 'Em atendimento' ? 'on' : ''}`} onClick={() => setStatusSel('Em atendimento')}>Em atendimento</span>
            <span className={`chip ${statusSel === 'Em avaliação' ? 'on' : ''}`} onClick={() => setStatusSel('Em avaliação')}>Em avaliação</span>
            <span className={`chip ${statusSel === 'Alta' ? 'on' : ''}`} onClick={() => setStatusSel('Alta')}>Alta</span>
          </div>
        </div>
        {optsConvenio.length > 0 && (
          <div className="fgroup"><label>Operadora</label>
            <select value={operadoraSel} onChange={e => setOperadoraSel(e.target.value)}>
              <option value="">Todas</option>{optsConvenio.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}
        <div className="fgroup"><label>Classificação</label>
          <select value={classifSel} onChange={e => setClassifSel(e.target.value)}>
            <option value="">Todas</option>{optsClassif.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {C.pacientType && (
          <div className="fgroup"><label>Tipo</label>
            <div className="chips">
              <span className={`chip ${novoReSel === '' ? 'on' : ''}`} onClick={() => setNovoReSel('')}>Todos</span>
              <span className={`chip ${novoReSel === 'Novo Paciente' ? 'on' : ''}`} onClick={() => setNovoReSel('Novo Paciente')}>Novo</span>
              <span className={`chip ${novoReSel === 'Re-Implantação' ? 'on' : ''}`} onClick={() => setNovoReSel('Re-Implantação')}>Re-implant.</span>
            </div>
          </div>
        )}
        {C.complexidade && optsComplexidade.length > 0 && (
          <div className="fgroup"><label>Complexidade</label>
            <select value={complexidadeSel} onChange={e => setComplexidadeSel(e.target.value)}>
              <option value="">Todas</option>{optsComplexidade.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}
        <div className="fgroup"><label>Liminar</label>
          <div className="chips">
            <span className={`chip ${liminarSel === '' ? 'on' : ''}`} onClick={() => setLiminarSel('')}>Todas</span>
            <span className={`chip ${liminarSel === 'Sim' ? 'on' : ''}`} onClick={() => setLiminarSel('Sim')}>Sim</span>
            <span className={`chip ${liminarSel === 'Não' ? 'on' : ''}`} onClick={() => setLiminarSel('Não')}>Não</span>
            <span className={`chip ${liminarSel === 'Não Informado' ? 'on' : ''}`} onClick={() => setLiminarSel('Não Informado')}>N/I</span>
          </div>
        </div>
        {optsCidades.length > 1 && (
          <div className="fgroup"><label>Cidade</label>
            <select value={cidadeSel} onChange={e => setCidadeSel(e.target.value)}>
              <option value="">Todas</option>{optsCidades.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}
        {C.redFlag && (
          <div className="fgroup"><label>Red Flag</label>
            <div className="chips">
              <span className={`chip ${redFlagSel === '' ? 'on' : ''}`} onClick={() => setRedFlagSel('')}>Todos</span>
              <span className={`chip ${redFlagSel === 'Sim' ? 'on' : ''}`} onClick={() => setRedFlagSel('Sim')}>Sim</span>
              <span className={`chip ${redFlagSel === 'Não' ? 'on' : ''}`} onClick={() => setRedFlagSel('Não')}>Não</span>
            </div>
          </div>
        )}
        <div className="fgroup"><label>Internação de</label><input type="date" value={periodoInicio} onChange={e => setPeriodoInicio(e.target.value)} /></div>
        <div className="fgroup"><label>até</label><input type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} /></div>
        <button className="clearbtn" onClick={limparFiltros}>Limpar filtros</button>
      </div>

      {loadingRows ? (
        <div style={{ textAlign: 'center', padding: 50, color: 'var(--indigo)', fontWeight: 600 }}>Carregando dados...</div>
      ) : (
        <>
          {/* ── KPIs ── */}
          <div className="section-h"><h2>Indicadores</h2><span className="desc">clique no card para detalhar a tabela · use "Detalhar" para ver o recorte por operadora</span></div>
          <div className="grid-kpi">

            <div className={`kpi ${openKpi === 'atend' ? 'open' : ''}`} style={{ '--c': 'var(--green)' }}>
              <div className="bar"></div>
              <div onClick={() => toggleDrill(r => r._sit === 'Em atendimento', 'Em Atendimento')}>
                <div className="lab">Em atendimento</div>
                <div className="v num">{kpis.emAtendimento}</div>
                <div className="meta">pacientes ativos</div>
              </div>
              <div className="exp" onClick={(e) => { e.stopPropagation(); setOpenKpi(openKpi === 'atend' ? null : 'atend'); }}>Detalhar <span className="caret">▾</span></div>
              <div className="detail"><div className="detail-inner">
                <RenderBarTable items={topOperadoras} color="var(--green)" />
              </div></div>
            </div>

            <div className={`kpi ${openKpi === 'aval' ? 'open' : ''}`} style={{ '--c': 'var(--amber)' }}>
              <div className="bar"></div>
              <div onClick={() => toggleDrill(r => r._sit === 'Em avaliação', 'Em Avaliação')}>
                <div className="lab">Em avaliação</div>
                <div className="v num">{kpis.emAvaliacao}</div>
                <div className="meta">aguardando liberação</div>
              </div>
              <div className="exp" onClick={(e) => { e.stopPropagation(); setOpenKpi(openKpi === 'aval' ? null : 'aval'); }}>Detalhar <span className="caret">▾</span></div>
              <div className="detail"><div className="detail-inner">
                <RenderBarTable items={solicitacoesPorOperadora} color="var(--amber)" />
              </div></div>
            </div>

            <div className={`kpi ${openKpi === 'liminar' ? 'open' : ''}`} style={{ '--c': 'var(--violet)' }}>
              <div className="bar"></div>
              <div onClick={() => toggleDrill(r => r._nLiminar === 'Sim' && r._sit === 'Em atendimento', 'Liminar')}>
                <div className="lab">Com liminar (ativos)</div>
                <div className="v num">{kpis.comLiminar}</div>
                <div className="meta">{kpis.emAtendimento ? Math.round(kpis.comLiminar/kpis.emAtendimento*100) : 0}% dos ativos</div>
                <div className="pbar"><i style={{ width: `${kpis.emAtendimento ? (kpis.comLiminar/kpis.emAtendimento*100) : 0}%` }}></i></div>
              </div>
              <div className="exp" onClick={(e) => { e.stopPropagation(); setOpenKpi(openKpi === 'liminar' ? null : 'liminar'); }}>Detalhar <span className="caret">▾</span></div>
              <div className="detail"><div className="detail-inner">
                <RenderBarTable items={liminarPorOperadora} color="var(--violet)" />
              </div></div>
            </div>

            <div className={`kpi ${openKpi === 'entradas' ? 'open' : ''}`} style={{ '--c': 'var(--indigo)' }}>
              <div className="bar"></div>
              <div onClick={() => toggleDrill(r => r._dInternacao && r._dInternacao >= currentPeriod.start && r._dInternacao <= currentPeriod.end, 'Entradas')}>
                <div className="lab">Entradas {periodTxt}</div>
                <div className="v num">{kpis.entradasMes}</div>
                <div className="meta">{kpis.entradasMesNovos} novos · {kpis.entradasMesReimplant} re-implant.</div>
              </div>
              <div className="exp" onClick={(e) => { e.stopPropagation(); setOpenKpi(openKpi === 'entradas' ? null : 'entradas'); }}>Detalhar <span className="caret">▾</span></div>
              <div className="detail"><div className="detail-inner">
                <RenderBarTable items={entradasPorOperadora} color="var(--indigo)" />
              </div></div>
            </div>

            <div className={`kpi ${openKpi === 'altas' ? 'open' : ''}`} style={{ '--c': 'var(--coral)' }}>
              <div className="bar"></div>
              <div onClick={() => toggleDrill(r => r._dSaida && r._dSaida >= currentPeriod.start && r._dSaida <= currentPeriod.end, 'Altas')}>
                <div className="lab">Altas / saídas {periodTxt}</div>
                <div className="v num">{kpis.altasMes}</div>
                <div className="meta">com data de saída no período</div>
              </div>
              <div className="exp" onClick={(e) => { e.stopPropagation(); setOpenKpi(openKpi === 'altas' ? null : 'altas'); }}>Detalhar <span className="caret">▾</span></div>
              <div className="detail"><div className="detail-inner">
                <RenderBarTable items={altasPorOperadora} color="var(--coral)" />
              </div></div>
            </div>

            <div className="kpi" style={{ '--c': '#06B6D4' }}>
              <div className="bar"></div>
              <div onClick={() => permanenciaStats.media !== null && toggleDrill(r => !!(r._dSaida && r._dInternacao), 'Permanência')}>
                <div className="lab">Permanência média</div>
                <div className="v num">{permanenciaStats.media !== null ? `${permanenciaStats.media}d` : '—'}</div>
                <div className="meta">{permanenciaStats.media !== null ? `mediana ${permanenciaStats.mediana}d · ${permanenciaStats.total} altas` : 'sem dados de alta no período'}</div>
              </div>
            </div>

            <div className="kpi" style={{ '--c': '#EF4444' }}>
              <div className="bar"></div>
              <div onClick={() => taxaDesfecho.hosp > 0 && toggleDrill(r => ['Hospitalização','Alta após hospitalização','Mudança H.C após Hospitalização'].includes(r._efCond || ''), 'Hospitalizados')}>
                <div className="lab">Taxa hospitalização {periodTxt}</div>
                <div className="v num">{taxaDesfecho.taxaHosp}%</div>
                <div className="meta">{taxaDesfecho.hosp} de {taxaDesfecho.totalAltas} altas</div>
                <div className="pbar"><i style={{ width: `${Math.min(taxaDesfecho.taxaHosp, 100)}%`, background: '#EF4444' }}></i></div>
              </div>
            </div>

            <div className="kpi" style={{ '--c': '#64748B' }}>
              <div className="bar"></div>
              <div onClick={() => taxaDesfecho.obito > 0 && toggleDrill(r => ['Óbito','Óbito após hospitalização'].includes(r._efCond || ''), 'Óbitos')}>
                <div className="lab">Taxa de óbito {periodTxt}</div>
                <div className="v num">{taxaDesfecho.taxaObito}%</div>
                <div className="meta">{taxaDesfecho.obito} de {taxaDesfecho.totalAltas} altas</div>
              </div>
            </div>

            <div className="kpi" style={{ '--c': '#10B981' }}>
              <div className="bar"></div>
              <div>
                <div className="lab">Saldo do censo {periodTxt}</div>
                <div className="v num" style={{ color: (kpis.entradasMes - kpis.altasMes) >= 0 ? '#10B981' : '#EF4444' }}>
                  {kpis.entradasMes - kpis.altasMes >= 0 ? '+' : ''}{kpis.entradasMes - kpis.altasMes}
                </div>
                <div className="meta">{kpis.entradasMes} entradas − {kpis.altasMes} altas</div>
              </div>
            </div>

            {kpis.redFlagAtivos > 0 && (
              <div className={`kpi ${openKpi === 'redflag' ? 'open' : ''}`} style={{ '--c': '#F43F5E' }}>
                <div className="bar"></div>
                <div onClick={() => toggleDrill(r => C.redFlag && String(r[C.redFlag] || '').trim() === 'Sim' && r._sit === 'Em atendimento', 'Red Flag')}>
                  <div className="lab">Red Flag (ativos)</div>
                  <div className="v num">{kpis.redFlagAtivos}</div>
                  <div className="meta">{kpis.emAtendimento ? Math.round(kpis.redFlagAtivos / kpis.emAtendimento * 100) : 0}% dos ativos</div>
                  <div className="pbar"><i style={{ width: `${kpis.emAtendimento ? Math.min(kpis.redFlagAtivos / kpis.emAtendimento * 100, 100) : 0}%`, background: '#F43F5E' }}></i></div>
                </div>
                <div className="exp" onClick={(e) => { e.stopPropagation(); setOpenKpi(openKpi === 'redflag' ? null : 'redflag'); }}>Detalhar <span className="caret">▾</span></div>
                <div className="detail"><div className="detail-inner">
                  <RenderBarTable items={redFlagPorOperadora} color="#F43F5E" />
                </div></div>
              </div>
            )}

          </div>

          {/* ── Gráficos ── */}
          <div className="section-h"><h2>Pacientes por operadora e perfil</h2><span className="desc">pacientes ativos, entradas e perfil clínico do recorte atual</span></div>
          <div className="grid-ch">

            <div className="card">
              <h3>Ativos por operadora <span className="mini-tag pac">pacientes</span></h3>
              <p className="cap">Pacientes em atendimento, por convênio</p>
              <RenderBarTable
                items={topOperadoras}
                color="var(--indigo)"
                activeFilterLabel={drillFilter?.label?.replace('Operadora: ', '')}
                onRowClick={(n) => toggleDrill(r => (r[C.operadora] || r['Operadora']) === n && r._sit === 'Em atendimento', `Operadora: ${n}`)}
              />
            </div>

            <div className="card">
              <h3>Novos por operadora <span className="mini-tag pac">pacientes</span></h3>
              <p className="cap">Pacientes de primeira vez, por convênio ({periodTxt})</p>
              <RenderBarTable
                items={topNovosPorOperadora}
                color="var(--teal)"
                activeFilterLabel={drillFilter?.label?.replace('NovoOp: ', '')}
                onRowClick={(n) => toggleDrill(r => (r[C.operadora] || r['Operadora']) === n && r._dInternacao && r._dInternacao >= currentPeriod.start && r._dInternacao <= currentPeriod.end, `NovoOp: ${n}`)}
              />
            </div>

            <div className="card">
              <h3>Reimplantados por operadora <span className="mini-tag pac">pacientes</span></h3>
              <p className="cap">Pacientes que saíram e reentraram, por convênio ({periodTxt})</p>
              <RenderBarTable items={topReimplantPorOperadora} color="var(--violet)" />
            </div>

            <div className="card">
              <h3>Em avaliação por operadora <span className="mini-tag pac">pacientes</span></h3>
              <p className="cap">Solicitações aguardando liberação, por convênio</p>
              <RenderBarTable items={solicitacoesPorOperadora} color="var(--amber)" />
            </div>

            <div className="card">
              <h3>Altas por operadora <span className="mini-tag orc">altas</span></h3>
              <p className="cap">Altas/saídas com data no período, por convênio ({periodTxt})</p>
              <RenderBarTable items={altasPorOperadora} color="var(--coral)" />
            </div>

            <div className="card">
              <h3>Classificação de atendimento <span className="mini-tag pac">ativos</span></h3>
              <p className="cap">ID, AD e demais classificações entre os ativos</p>
              <RenderBarTable
                items={chartClassif}
                color="var(--indigo)"
                showLiminar
                activeFilterLabel={drillFilter?.label?.replace('Classif: ', '')}
                onRowClick={(n) => toggleDrill(r => r._nClassif === n && r._sit === 'Em atendimento', `Classif: ${n}`)}
              />
            </div>

            <div className="card">
              <h3>Complexidade <span className="mini-tag pac">ativos</span></h3>
              <p className="cap">Entre os pacientes em atendimento no recorte atual</p>
              <RoscaComTotal data={chartComplexidade} cores={{ 'Sem info': '#C9CFCB', 'Baixa Complexidade': '#10B981', 'Média Complexidade': '#F59E0B', 'Alta Complexidade': '#EF4444' }} />
            </div>

            <div className="card">
              <h3>Novo × Re-implant. <span className="mini-tag pac">ativos</span></h3>
              <p className="cap">Proporção entre pacientes novos e reimplantados</p>
              <RoscaComTotal data={chartNovoRe} cores={{ 'Novo Paciente': '#6366F1', 'Re-Implantação': '#8B5CF6' }} />
            </div>

            <div className="card">
              <h3>Classificação por filial <span className="mini-tag pac">recorte</span></h3>
              <p className="cap">ID, AD e demais classificações em cada unidade</p>
              <div className="chart-box sm">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classifPorFilial} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false}
                      tickFormatter={n => { const d = classifPorFilial.find(x => x.name === n); return d ? `${n} · ${CLASSIF_KEYS.reduce((s, k) => s + (d[k] || 0), 0)}` : n; }} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                    <RTooltip content={<LightTip />} cursor={{ fill: 'transparent' }} />
                    <Legend wrapperStyle={{ fontSize: '11px', color: 'var(--muted)' }} iconType="circle"
                      formatter={k => `${k} (${classifPorFilial.reduce((s, d) => s + (d[k] || 0), 0)})`} />
                    {CLASSIF_KEYS.map(k => (
                      <Bar key={k} dataKey={k} stackId="a" fill={CLASSIFCOL[k]} radius={[0,0,0,0]} barSize={28} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Entradas × Desfecho por Operadora ── */}
            <div className="card wide">
              <h3>Entradas por operadora × desfecho <span className="mini-tag pac">entradas</span></h3>
              <p className="cap">Entrada = paciente com Data de Internação no período ({periodTxt}). Cor = situação atual do episódio.</p>
              <div className="chart-box tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartEntradasOperadora} layout="vertical" margin={{ top: 0, right: 20, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--line)" />
                    <XAxis type="number" tick={{fontSize: 11, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: 'var(--ink)'}} width={140} />
                    <RTooltip content={<LightTip />} cursor={{fill: 'transparent'}} />
                    {DESFECHO_KEYS.map(k => (
                      <Bar key={k} dataKey={k} stackId="a" fill={SITCOL[k]} radius={[0,0,0,0]} barSize={20} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="legend">
                {DESFECHO_KEYS.map(k => <span key={k}><i style={{background: SITCOL[k]}}></i>{k}</span>)}
              </div>
            </div>

            {/* ── Evolução de entradas (geral, com toggle) ── */}
            <div className="card wide">
              <h3 style={{justifyContent: 'space-between'}}>
                <span>Evolução de entradas por operadora</span>
                <span style={{display:'flex',gap:16,alignItems:'center'}}>
                  <span className="chips">
                    <span className={`chip ${evoGran === 'M' ? 'on' : ''}`} onClick={() => setEvoGran('M')}>Mês</span>
                    <span className={`chip ${evoGran === 'D' ? 'on' : ''}`} onClick={() => setEvoGran('D')}>Dia</span>
                  </span>
                  <span className="chips">
                    <span className={`chip ${evoMode === 'periodo' ? 'on' : ''}`} onClick={() => setEvoMode('periodo')}>Por período</span>
                    <span className={`chip ${evoMode === 'acum' ? 'on' : ''}`} onClick={() => setEvoMode('acum')}>Acumulado</span>
                  </span>
                </span>
              </h3>
              <p className="cap">Todas as entradas (novos + re-implantações), 6 maiores convênios do recorte + Outros</p>
              <div className="chart-box" style={{height: 330}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartEvolucao} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
                    <XAxis dataKey="label" tick={{fontSize: 10, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 11, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <RTooltip content={<LightTip />} />
                    <Legend wrapperStyle={{fontSize:'11.5px',color:'var(--muted)',paddingTop:10}} iconType="circle" />
                    {evolucaoSeries.map((s, i) => (
                      <Line key={s} type="monotone" dataKey={s} stroke={OPPAL[i % OPPAL.length]} strokeWidth={2} dot={{r: chartEvolucao.length > 40 ? 0 : 3}} activeDot={{r: 5}} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="note">Use o filtro de datas para focar num período.</div>
            </div>

            {/* ── Entradas de novos por mês ── */}
            <div className="card wide">
              <h3>Entradas de novos por mês <span className="mini-tag pac">pacientes</span></h3>
              <p className="cap">Novos pacientes por mês de internação — 6 maiores operadoras do recorte + Outras</p>
              <div className="chart-box" style={{height: 300}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={novosEvolucao} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
                    <XAxis dataKey="label" tick={{fontSize: 10, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 11, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <RTooltip content={<LightTip />} />
                    <Legend wrapperStyle={{fontSize:'11.5px',color:'var(--muted)',paddingTop:10}} iconType="circle" />
                    {novosEvolucaoSeries.map((s, i) => (
                      <Line key={s} type="monotone" dataKey={s} stroke={OPPAL[i % OPPAL.length]} strokeWidth={2} dot={{r: novosEvolucao.length > 40 ? 0 : 3}} activeDot={{r: 5}} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Reimplantações por mês ── */}
            <div className="card wide">
              <h3>Reimplantações por mês <span className="mini-tag orc">eventos</span></h3>
              <p className="cap">Reentradas (reimplantações) por mês de internação — 6 maiores operadoras do recorte + Outras</p>
              <div className="chart-box" style={{height: 300}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reimplantEvolucao} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
                    <XAxis dataKey="label" tick={{fontSize: 10, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 11, fill: 'var(--muted)'}} axisLine={false} tickLine={false} />
                    <RTooltip content={<LightTip />} />
                    <Legend wrapperStyle={{fontSize:'11.5px',color:'var(--muted)',paddingTop:10}} iconType="circle" />
                    {reimplantEvolucaoSeries.map((s, i) => (
                      <Line key={s} type="monotone" dataKey={s} stroke={OPPAL[i % OPPAL.length]} strokeWidth={2} dot={{r: reimplantEvolucao.length > 40 ? 0 : 3}} activeDot={{r: 5}} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Tipos de saída e TISS ── */}
            <div className="card">
              <h3>Tipos de saída — desfecho <span className="mini-tag orc">altas</span></h3>
              <p className="cap">Desfecho operacional da saída ({periodTxt}) · total: <strong>{chartCondicaoSaida.reduce((s, x) => s + x.value, 0)}</strong></p>
              <div className="chart-box tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartCondicaoSaida} layout="vertical" margin={{ top: 0, right: 20, left: -20, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: 'var(--ink)'}} width={140} />
                    <RTooltip content={<LightTip />} cursor={{fill: 'transparent'}} />
                    <Bar dataKey="value" fill="var(--coral)" radius={4} barSize={16}>
                      <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 700, fill: 'var(--ink)' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card">
              <h3>Tipos de alta — TISS <span className="mini-tag orc">altas</span></h3>
              <p className="cap">Classificação clínica/administrativa da alta ({periodTxt}) · total: <strong>{chartAltaTiss.reduce((s, x) => s + x.value, 0)}</strong></p>
              <div className="chart-box tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartAltaTiss} layout="vertical" margin={{ top: 0, right: 20, left: -20, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: 'var(--ink)'}} width={140} />
                    <RTooltip content={<LightTip />} cursor={{fill: 'transparent'}} />
                    <Bar dataKey="value" fill="var(--indigo)" radius={4} barSize={16}>
                      <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 700, fill: 'var(--ink)' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Dispositivos ── */}
            {chartDispositivos.length > 0 && (
              <div className="card wide">
                <h3>Dispositivos em uso <span className="mini-tag pac">ativos</span></h3>
                <p className="cap">Pacientes em atendimento com cada dispositivo</p>
                <RenderBarTable items={chartDispositivos} color="var(--amber)" mostrarTotal={false} totalActive={ativosFiltered.length} activeFilterLabel={drillFilter?.label?.replace('Dispositivo: ', '')} onRowClick={(n) => toggleDrill(r => String(r[`Tem ${n}`]||r[`tem ${n}`]||'').trim()==='Sim' && r._sit === 'Em atendimento', `Dispositivo: ${n}`)} />
              </div>
            )}

            {/* ── Multi-dispositivos ── */}
            {multiDispositivosTotal > 0 && (
              <div className="card">
                <h3>Pacientes multi-dispositivos <span className="mini-tag pac">ativos</span></h3>
                <p className="cap">Pacientes com 2 ou mais dispositivos simultâneos</p>
                <div style={{ padding: '16px 0 12px' }}>
                  <div className="v num" style={{ fontSize: '2.2rem', fontWeight: 900, color: '#F59E0B', lineHeight: 1 }}>{multiDispositivosTotal}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 4, marginBottom: 16 }}>
                    {ativosFiltered.length ? Math.round(multiDispositivosTotal / ativosFiltered.length * 100) : 0}% dos pacientes ativos
                  </div>
                </div>
                <RenderBarTable
                  items={C.dispositivos.map(d => ({
                    name: d.replace(/tem /i, '').trim(),
                    total: ativosFiltered.filter(r => String(r[d] || '').trim() === 'Sim').length,
                  })).filter(x => x.total > 0).sort((a, b) => b.total - a.total)}
                  color="var(--amber)"
                  totalActive={ativosFiltered.length}
                  mostrarTotal={false}
                />
              </div>
            )}

            {/* ── Qualidade assistencial — desfecho ── */}
            {taxaDesfecho.totalAltas > 0 && (
              <div className="card">
                <h3>Qualidade assistencial — desfecho <span className="mini-tag orc">altas</span></h3>
                <p className="cap">Causas de saída como % do total de altas ({taxaDesfecho.totalAltas} altas no período)</p>
                <div className="btable" style={{ padding: '12px 0' }}>
                  {[
                    { lab: 'Hospitalização',       v: taxaDesfecho.taxaHosp,   n: taxaDesfecho.hosp,   c: '#EF4444', fn: r => ['Hospitalização','Alta após hospitalização','Mudança H.C após Hospitalização'].includes(r._efCond||'') },
                    { lab: 'Óbito',                v: taxaDesfecho.taxaObito,  n: taxaDesfecho.obito,  c: '#64748B', fn: r => ['Óbito','Óbito após hospitalização'].includes(r._efCond||'') },
                    { lab: 'Cancelado / Suspenso', v: taxaDesfecho.taxaCancel, n: taxaDesfecho.cancel, c: '#8B5CF6', fn: r => ['Cancelado pelo convenio','Suspenso pela Operadora','Orçamento Inválido'].includes(r._efCond||'') },
                  ].map(({ lab, v, n, c, fn }) => (
                    <div key={lab} className="r" style={{ marginBottom: 14, cursor: n > 0 ? 'pointer' : 'default' }} onClick={() => n > 0 && toggleDrill(fn, lab)}>
                      <div className="top"><span className="nm">{lab}</span><span className="ct num">{v}% — {n} pac.</span></div>
                      <div className="track"><i style={{ width: `${Math.min(v, 100)}%`, background: c }}></i></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Permanência média por complexidade ── */}
            {permanenciaPorComplexidade.length > 0 && (
              <div className="card">
                <h3>Permanência por complexidade <span className="mini-tag pac">dias</span></h3>
                <p className="cap">Média de dias em atendimento até a alta, por nível de complexidade</p>
                <div className="chart-box sm">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={permanenciaPorComplexidade} layout="vertical" margin={{ top: 0, right: 55, left: -20, bottom: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--ink)' }} width={160} />
                      <RTooltip content={<LightTip />} cursor={{ fill: 'transparent' }} />
                      <Bar dataKey="media" name="Média (dias)" fill="#06B6D4" radius={4} barSize={18}>
                        <LabelList dataKey="media" position="right" formatter={v => `${v} dias`} style={{ fontSize: 12, fontWeight: 700, fill: 'var(--ink)' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── Permanência média por operadora ── */}
            {permanenciaPorOperadora.length > 0 && (
              <div className="card wide">
                <h3>Permanência média por operadora <span className="mini-tag pac">dias</span></h3>
                <p className="cap">Média de dias em atendimento por convênio — apenas convênios com ≥3 altas no recorte</p>
                <div className="chart-box tall">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={permanenciaPorOperadora} layout="vertical" margin={{ top: 0, right: 55, left: -20, bottom: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--ink)' }} width={140} />
                      <RTooltip content={<LightTip />} cursor={{ fill: 'transparent' }} />
                      <Bar dataKey="media" name="Média (dias)" fill="#0EA5E9" radius={4} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── Saldo de censo por mês ── */}
            {saldoPorMes.length > 1 && (
              <div className="card wide">
                <h3>Saldo de censo por mês <span className="mini-tag pac">pacientes</span></h3>
                <p className="cap">Entradas versus altas mensais — saldo verde = crescimento, vermelho = retração</p>
                <div className="chart-box" style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={saldoPorMes} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                      <RTooltip content={<LightTip />} />
                      <Legend wrapperStyle={{ fontSize: '11px', color: 'var(--muted)' }} iconType="circle" />
                      <Bar dataKey="Entradas" fill="#6366F1" radius={[4,4,0,0]} barSize={14} />
                      <Bar dataKey="Altas"    fill="#FB7185" radius={[4,4,0,0]} barSize={14} />
                      <Bar dataKey="Saldo"    fill="#10B981" radius={[4,4,0,0]} barSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── Distribuição por cidade ── */}
            {chartCidades.length > 0 && (
              <div className="card">
                <h3>Distribuição por cidade <span className="mini-tag pac">ativos</span></h3>
                <p className="cap">Pacientes em atendimento por município</p>
                <RenderBarTable
                  items={chartCidades}
                  color="var(--teal)"
                  totalActive={ativosFiltered.length}
                  activeFilterLabel={drillFilter?.label?.replace('Cidade: ', '')}
                  onRowClick={(n) => toggleDrill(r => r._nCidade === n && r._sit === 'Em atendimento', `Cidade: ${n}`)}
                />
              </div>
            )}

          </div>

          {/* ── Tabela detalhada ── */}
          <div ref={tableRef} className="card wide" style={{marginTop: 26}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <h3 style={{margin: 0, marginBottom: 5}}>{drillFilter ? `Detalhes — ${drillFilter.label}` : 'Dados detalhados'}</h3>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{drillData.length.toLocaleString('pt-BR')} registros · Pág {tablePage} de {totalPag}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="chip" disabled={tablePage <= 1} onClick={() => setTablePage(p => p - 1)} style={{opacity: tablePage<=1 ? 0.5 : 1}}>← Anterior</button>
                <button className="chip" disabled={tablePage >= totalPag} onClick={() => setTablePage(p => p + 1)} style={{opacity: tablePage>=totalPag ? 0.5 : 1}}>Próximo →</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="detailed">
                <thead><tr>{tabelaColunas.map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {rowsPag.map((row, i) => (
                    <tr key={i}>
                      {tabelaColunas.map(c => <td key={c} title={row[c]}>{row[c] || '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="footnote">
            {uploadAtual ? `Fonte: ${uploadAtual.fileName} · referência ${uploadAtual.dataReferencia} · ${uploadAtual.totalRows?.toLocaleString('pt-BR')} registros carregados` : ''}
          </p>
        </>
      )}

    </div>
    </div>
  );
}
